import React, { FunctionComponent } from 'react';
import { MappedComponentProperties, MapTo } from '@adobe/aem-react-editable-components';
import { getRemoteGuardProps } from '@quantum/tools/guard';
import { BrowserRouter } from 'react-router-dom';
import { withSingleSpaAsync } from '../../components/SpaComponent';
import withCustomMarker from '../../hoc/withCustomMarker';
import withQueryMarker from '../../hoc/withQueryMarker';
import RemoteAppProps, { RemoteAppPropsClient } from '../../../../types/RemoteAppProps';
import withEmptyMarker from '../../hoc/withEmptyMarker';
import { onAnalyticsEvent } from '../../../../utils/analytics';
import { RemoteUclContext } from '../../../../modules/remote-ucl/context';
import { Context } from '../../../../assembler-server/types/Context';
import { registerMfeForRender, loadMfe } from '../../../../modules/mfe-ssr/client';
import withMainContainer from '../../hoc/withMainContainer';
import { App } from './App';

function UCLAppClient(props: RemoteAppProps) {
  const { AnalyticsContext, AemStaticPage } = props.remoteUcl;
  return (
    <RemoteUclContext.Provider value={props.remoteUcl}>
      <AnalyticsContext.Provider value={{ onAnalyticsEvent }}>
        <BrowserRouter>
          <App {...props}>
            <AemStaticPage />
          </App>
        </BrowserRouter>
      </AnalyticsContext.Provider>
    </RemoteUclContext.Provider>
  );
}

export default withSingleSpaAsync(
  UCLAppClient,
  window.shouldClientSideRender,
  async ({ featureFlags, rootReducer, guard, country, language, brand, remoteUcl, ssrMarkers }: RemoteAppPropsClient) => {
    let Component = withCustomMarker(remoteUcl.Marker);
    if (featureFlags.CONSOLIDATE_QUERY) {
      const module = await import('../../../../assembler-server/redux/graphSlice');
      rootReducer.inject({ reducerPath: module.default.reducerPath, reducer: module.default.reducer });
      Component = withQueryMarker(Component);
    }

    MapTo(remoteUcl.MARKER_RESOURCE_TYPE)(withEmptyMarker(Component) as FunctionComponent<MappedComponentProperties>);
    if (remoteUcl.EditableContainer) {
      MapTo(remoteUcl.EditableContainer.resourceType)(
        withMainContainer(remoteUcl.EditableContainer) as FunctionComponent<MappedComponentProperties>
      );
    }

    // Load all SSR MFE
    await Promise.all(ssrMarkers.map((marker) => loadMfe(marker).then((Component) => registerMfeForRender(marker, Component))));

    const props = {} as Record<string, object>;
    if (guard) {
      props.guardProps = await getRemoteGuardProps(guard, { country, language, brand } as Context).catch((e) => {
        console.error('Cannot fetch remote guard', e);
        return {
          evaluate: () => {
            throw new Error(`Cannot fetch remote guard: ${JSON.stringify(guard)}`);
          }
        };
      });
    }

    return props;
  }
);
